"use client"

import "./App.css"
import BrainCanvas from "./components/BrainCanvas"
import Navbar from "./components/Navbar"
import ColoredNeuralNetwork from "./components/NeuralNetwork"
import Textbox from "./components/Textbox"
import BrainInsights from "./components/brain-insights"
import Sidebar from "./components/Sidebar"
import Dashboard from "./components/Dashboard"
import ActivityHistory from "./components/ActivityHistory"
import StatsCards from "./components/StatsCards"
import ScenariosView from "./components/ScenariosView"
import LearnView from "./components/LearnView"
import WellnessView from "./components/WellnessView"
import AboutView from "./components/AboutView"
import LandingPage from "./components/LandingPage"
import LoginPage from "./components/LoginPage"
import "./components/brain-insights.css"
import { useState, useEffect } from "react"
import { analyzeTask, getMockAnalysis } from "./components/api-service"
import { ThemeProvider } from "./components/theme-provider"
import { motion, AnimatePresence } from "framer-motion"

function App() {
  const [currentActivity, setCurrentActivity] = useState("")
  const [aiInsights, setAiInsights] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [useMockData, setUseMockData] = useState(true)
  const [moodScenario, setMoodScenario] = useState(null)
  const [activeView, setActiveView] = useState("dashboard")
  const [activityHistory, setActivityHistory] = useState([])
  const [appState, setAppState] = useState("landing") // landing, login, dashboard

  // Fetch AI insights when activity changes
  useEffect(() => {
    if (!currentActivity) {
      setAiInsights(null)
      return
    }

    const fetchInsights = async () => {
      setIsLoading(true)
      setError(null)

      try {
        let data
        if (useMockData) {
          // Use mock data for testing - pass the current activity!
          data = getMockAnalysis(currentActivity)
          // Simulate network delay
          await new Promise((resolve) => setTimeout(resolve, 1000))
        } else {
          // Use real API
          data = await analyzeTask(currentActivity)
        }
        setAiInsights(data)
      } catch (err) {
        console.error("Failed to fetch AI insights:", err)
        setError("Failed to analyze brain activity. Please try again or enable mock data.")
        // Fall back to mock data if API fails
        if (!useMockData) {
          const mockData = getMockAnalysis(currentActivity)
          setAiInsights(mockData)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchInsights()
  }, [currentActivity, useMockData])

  const handleAddActivity = (activity) => {
    setCurrentActivity(activity)
    // Reset mood scenario when a new activity is added
    setMoodScenario(null)
    // Add to history
    setActivityHistory(prev => [{
      id: Date.now(),
      name: activity,
      timestamp: new Date(),
      regions: aiInsights?.brain_regions || []
    }, ...prev].slice(0, 20)) // Keep last 20 activities
  }

  const handleSelectActivity = (activity) => {
    setCurrentActivity(activity.name || activity)
    setActiveView("brain")
  }

  const handleDeleteActivity = (id) => {
    setActivityHistory(prev => prev.filter(a => a.id !== id))
  }

  const toggleMockData = () => {
    setUseMockData(!useMockData)
    // Clear existing insights to force a refresh when toggling
    setAiInsights(null)
    if (currentActivity) {
      // Rerun the analysis with the current activity
      setCurrentActivity((prev) => {
        // This is a trick to force the useEffect to run again
        // even if the activity hasn't changed
        const temp = ""
        setTimeout(() => setCurrentActivity(prev), 0)
        return temp
      })
    }
  }

  // Map AI brain regions to app's brain regions for visualization
  const mapAiRegionsToAppRegions = (aiRegions) => {
    if (!aiRegions) return []

    // This mapping helps translate the AI model's region names to the ones used in BrainCanvas
    const regionMapping = {
      "Frontal Lobe": "frontal lobe",
      "Prefrontal Cortex": "frontal lobe",
      "Temporal Lobe": "temporal lobe",
      "Parietal Lobe": "parietal lobe",
      "Occipital Lobe": "occipital lobe",
      Cerebellum: "cerebellum",
      Brainstem: "brainstem",
      Hippocampus: "hippocampus",
      Amygdala: "amygdala",
      "Motor Cortex": "frontal lobe",
      "Broca's Area": "brocasArea", // Fixed mapping
      "Wernicke's Area": "wernickesArea", // Fixed mapping
      "Visual Cortex": "occipital lobe",
      "Auditory Cortex": "temporal lobe",
      "Limbic System": "hippocampus amygdala",
      "Basal Ganglia": "brainstem",
      Thalamus: "brainstem",
      Hypothalamus: "brainstem",
      "Corpus Callosum": "frontal lobe temporal lobe parietal lobe occipital lobe",
      "Reticular Formation": "brainstem",
      "Superior Colliculus": "brainstem",
      "Inferior Colliculus": "brainstem",
      "Frontal Eye Fields": "frontal lobe",
      "Superior Temporal Gyrus": "superiorTemporalGyrus", // Fixed mapping
      "Inferior Frontal Gyrus": "inferiorFrontalGyrus", // Fixed mapping
      "Angular Gyrus": "parietalLobe", // Added mapping
    }

    // Map and flatten the regions, removing duplicates
    return [
      ...new Set(
        aiRegions.map((region) => regionMapping[region] || region.toLowerCase()).flatMap((region) => region.split(" ")),
      ),
    ]
  }

  // Handle mood scenario selection
  const handleMoodScenario = (scenario) => {
    setMoodScenario(scenario)

    // Set appropriate activity based on scenario
    if (scenario === "happy-to-sad") {
      setCurrentActivity("mood change: happy to sad")
    } else if (scenario === "grief-to-thoughts") {
      setCurrentActivity("mood change: grief to deep thoughts")
    }
  }

  // Get custom insights for mood scenarios
  useEffect(() => {
    if (!moodScenario) return

    const getMoodScenarioData = async () => {
      setIsLoading(true)

      // Simulate delay
      await new Promise((resolve) => setTimeout(resolve, 1000))

      let scenarioData
      if (moodScenario === "happy-to-sad") {
        scenarioData = {
          brain_regions: ["Amygdala", "Prefrontal Cortex", "Limbic System", "Hypothalamus"],
          cognitive_effects: [
            "Emotional Regulation Shift",
            "Decreased Dopamine Activity",
            "Increased Cortisol Release",
            "Altered Serotonin Signaling",
          ],
          damage_risks: ["Prolonged Negative Mood", "Stress Response Activation", "Cognitive Distortion"],
          stress_level: "elevated",
          mood_transition: {
            from: "happy",
            to: "sad",
            regions_activation: [
              { region: "Amygdala", change: "increasing" },
              { region: "Prefrontal Cortex", change: "decreasing" },
            ],
            neurotransmitters: [
              { name: "Dopamine", change: "decreasing" },
              { name: "Serotonin", change: "decreasing" },
            ],
          },
        }
      } else if (moodScenario === "grief-to-thoughts") {
        scenarioData = {
          brain_regions: ["Amygdala", "Prefrontal Cortex", "Anterior Cingulate Cortex", "Hippocampus"],
          cognitive_effects: ["Emotional Processing", "Memory Consolidation", "Abstract Reasoning", "Self-Reflection"],
          damage_risks: ["Rumination", "Emotional Exhaustion", "Memory Distortion"],
          stress_level: "fluctuating",
          mood_transition: {
            from: "grief",
            to: "deep thoughts",
            regions_activation: [
              { region: "Amygdala", change: "decreasing" },
              { region: "Prefrontal Cortex", change: "increasing" },
              { region: "Hippocampus", change: "increasing" },
            ],
            neurotransmitters: [
              { name: "Norepinephrine", change: "decreasing" },
              { name: "Glutamate", change: "increasing" },
            ],
          },
        }
      }

      setAiInsights(scenarioData)
      setIsLoading(false)
    }

    getMoodScenarioData()
  }, [moodScenario])

  return (
    <ThemeProvider>
      {appState === "landing" && (
        <LandingPage onGetStarted={() => setAppState("login")} />
      )}

      {appState === "login" && (
        <LoginPage onLogin={() => setAppState("dashboard")} />
      )}

      {appState === "dashboard" && (
        <div className="app-container modern-layout">
          <Sidebar activeView={activeView} setActiveView={setActiveView} />
          
          <div className="main-content">
            <Navbar onNavigate={(view) => setActiveView(view)} />
          
          <motion.main
            className="content-area"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Floating Search Bar */}
            <motion.div 
              className="floating-search"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Textbox onAddActivity={handleAddActivity} />
            </motion.div>

            {/* Control Panel */}
            <motion.div 
              className="modern-control-panel glass-card"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="control-section">
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="mockDataToggle"
                    checked={useMockData}
                    onChange={toggleMockData}
                  />
                  <label htmlFor="mockDataToggle">
                    <span className="toggle-label">Use Mock Data</span>
                  </label>
                </div>
              </div>

              <div className="scenario-section">
                <h5>Quick Scenarios</h5>
                <div className="scenario-buttons-modern">
                  <motion.button
                    className={`scenario-btn ${moodScenario === "happy-to-sad" ? "active" : ""}`}
                    onClick={() => handleMoodScenario("happy-to-sad")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    😊 → 😢 Mood Shift
                  </motion.button>
                  <motion.button
                    className={`scenario-btn ${moodScenario === "grief-to-thoughts" ? "active" : ""}`}
                    onClick={() => handleMoodScenario("grief-to-thoughts")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    😔 → 🤔 Deep Thinking
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {error && (
              <motion.div 
                className="error-banner"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
              </motion.div>
            )}

            {/* Dynamic Content Based on Active View */}
            <AnimatePresence mode="wait">
              {activeView === "dashboard" && (
                <motion.div
                  key="dashboard"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <StatsCards insights={aiInsights} activityCount={activityHistory.length} />
                  <Dashboard insights={aiInsights} activityHistory={activityHistory} />
                </motion.div>
              )}

              {activeView === "brain" && (
                <motion.div
                  key="brain"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="brain-view-grid">
                    {/* Brain Model */}
                    <div className="visualization-card glass-card">
                      <div className="card-header-modern">
                        <h3>🧠 Brain Region Activity</h3>
                      </div>
                      <BrainCanvas
                        activities={
                          aiInsights?.brain_regions
                            ? mapAiRegionsToAppRegions(aiInsights.brain_regions)
                            : currentActivity
                              ? [currentActivity]
                              : []
                        }
                        brainRegionLabels={aiInsights?.brain_regions || []}
                        moodTransition={aiInsights?.mood_transition || null}
                      />
                      {!currentActivity && (
                        <div className="instructions-overlay-modern">
                          <div className="instructions-card-modern">
                            <h3>🎯 Start Analyzing</h3>
                            <p>Enter an activity to visualize brain regions</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Neural Network */}
                    <div className="visualization-card glass-card">
                      <div className="card-header-modern">
                        <h3>⚡ Neural Network</h3>
                      </div>
                      <ColoredNeuralNetwork
                        activity={aiInsights?.cognitive_effects?.length > 0 ? aiInsights.cognitive_effects[0] : currentActivity}
                        moodTransition={aiInsights?.mood_transition || null}
                      />
                      {!currentActivity && (
                        <div className="instructions-overlay-modern">
                          <div className="instructions-card-modern">
                            <h3>🌐 Neural Pathways</h3>
                            <p>Watch neural connections light up</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Insights */}
                  {(currentActivity || isLoading) && (
                    <motion.div 
                      className="insights-modern"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <BrainInsights
                        insights={aiInsights}
                        isLoading={isLoading}
                        moodTransition={aiInsights?.mood_transition || null}
                      />
                    </motion.div>
                  )}
                </motion.div>
              )}

              {activeView === "analytics" && (
                <motion.div
                  key="analytics"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Dashboard insights={aiInsights} activityHistory={activityHistory} />
                </motion.div>
              )}

              {activeView === "history" && (
                <motion.div
                  key="history"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ActivityHistory 
                    activities={activityHistory}
                    onSelectActivity={handleSelectActivity}
                    onDeleteActivity={handleDeleteActivity}
                  />
                </motion.div>
              )}

              {activeView === "scenarios" && (
                <motion.div
                  key="scenarios"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ScenariosView onSelectScenario={(scenario) => {
                    if (scenario.id === "happy-sad") {
                      handleMoodScenario("happy-to-sad")
                    } else if (scenario.id === "grief-thoughts") {
                      handleMoodScenario("grief-to-thoughts")
                    }
                    setActiveView("brain")
                  }} />
                </motion.div>
              )}

              {activeView === "learn" && (
                <motion.div
                  key="learn"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <LearnView />
                </motion.div>
              )}

              {activeView === "wellness" && (
                <motion.div
                  key="wellness"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <WellnessView />
                </motion.div>
              )}

              {activeView === "about" && (
                <motion.div
                  key="about"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <AboutView />
                </motion.div>
              )}

              {activeView === "settings" && (
                <motion.div
                  key="settings"
                  className="coming-soon-view glass-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <h2>🚀 Coming Soon</h2>
                  <p>The settings section is under development</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.main>
        </div>
      </div>
      )}
    </ThemeProvider>
  )
}

export default App
